import styles from 'signer-app/signature-modal/upload/signature-edit-area.module.css';

import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Text } from '@dropbox/dig-components/typography';
import { Button, IconButton } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import {
  RotateRightLine,
  DeleteLine,
  ZoomOutLine,
  ZoomInLine,
  ShapeRotateRightLine,
} from '@dropbox/dig-icons/assets';
import { Chip } from '@dropbox/dig-components/chip';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import CONSTANTS from 'signer-app/signature-modal/constants';

import Croppable, { Bounds } from 'signer-app/signature-modal/upload/croppable';
import { ButtonsDivider } from 'signer-app/signature-modal/common/buttons-divider';
import { SignatureEditAreaHelper } from 'signer-app/signature-modal/helpers/signature-edit-area-helper';
import {
  Dimensions,
  scaleSignatureToFit,
} from 'signer-app/signature-modal/utils/scale-signature-to-fit';
import { Signature } from 'signer-app/signature-modal/types';
import ChipButtonsWrapper from '../common/chip-buttons-wrapper';
import { useFeatureFlag } from '../../../js/sign-components/common/feature-flag';

const messages = defineMessages({
  signaturePreview: {
    id: '47c6e18719e09da1e05335d8e96cb13c4272bbb95a81726ca96f8a4146400861',
    description:
      'Description of image presenting signature preview in edit mode',
    defaultMessage: 'Signature preview',
  },
  increaseContrast: {
    id: '394a9b3d2944c3c0b2d691df8681bea70f76750c1da519bb66289c7223b48766',
    description:
      'Screen reader label for a button that increases contrast of the uploaded signature image.',
    defaultMessage:
      'Increase contrast, current contrast {contrastPercentage, number, percent}',
  },
  decreaseContrast: {
    id: '9b8f15ea25824e4aa9467cb5eb07cad33f5637f64aada839a011b5678601f2ea',
    description:
      'Screen reader label for a button that decreases contrast of the uploaded signature image.',
    defaultMessage:
      'Decrease contrast, current contrast {contrastPercentage, number, percent}',
  },
  rotateAriaLabel: {
    id: '185373301d3f45eeea0525bd4fbcb857bbec94e9003a16a322244f5e9c739892',
    description:
      'Screen reader label for a button which rotates an image clockwise when pressed, containing information how many degrees it is currently rotated.',
    defaultMessage: 'Rotate, signature rotated {rotation, number} degrees',
  },
  deleteAriaLabel: {
    id: 'ac14eb6f69c138ba419850f72ed4229c4de7e291467ad72ca8060cf2732419d1',
    description:
      'Screen reader label for a button which deletes am image when pressed.',
    defaultMessage: 'Delete signature',
  },
});

export interface SignatureEditAreaProps {
  signature: Signature;
  getSignatureUrl: (
    signature: Signature,
    contrast: number,
    rotation: number,
  ) => string;
  clearUploadedSignature: () => void;
  contrast: number;
  rotation: number;
  isMobile: boolean;
  onPreviewChange: (
    data: Partial<{
      bounds: Bounds | null;
      contrast: number;
      rotation: number;
    }>,
  ) => void;
}

const PREVIEW_AREA_DIMENSIONS: Dimensions = {
  width: 398,
  height: 120,
};

const SignatureEditArea: FC<SignatureEditAreaProps> = ({
  contrast,
  rotation,
  signature,
  getSignatureUrl,
  clearUploadedSignature,
  onPreviewChange,
  isMobile,
}) => {
  const intl = useIntl();
  const cache = useRef(new Map<string, HTMLImageElement>()).current;
  const increaseContrastRef = useRef<HTMLElement>(null);
  const decreaseContrastRef = useRef<HTMLElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const isNewSignerApp = useFeatureFlag('sign_core_20240330_new_signer_app');

  const contrastPercentage =
    (contrast - CONSTANTS.CONTRAST_MIN_THRESHOLD) /
    (CONSTANTS.CONTRAST_MAX_THRESHOLD - CONSTANTS.CONTRAST_MIN_THRESHOLD);

  useEffect(() => {
    // initially focus on the decrease contrast button
    decreaseContrastRef.current?.focus();
  }, []);

  useEffect(() => {
    if (contrast <= CONSTANTS.CONTRAST_MIN_THRESHOLD) {
      // focus on increase button if contrast is already min
      increaseContrastRef.current?.focus();
    } else if (contrast >= CONSTANTS.CONTRAST_MAX_THRESHOLD) {
      // move focus to the decrease contrast button if max contrast reached
      decreaseContrastRef.current?.focus();
    }
  }, [contrast]);

  const previewStyle = useMemo(() => {
    if (!signature) return {};

    const url = getSignatureUrl(signature, contrast, rotation);
    const backgroundImage = `url(${url})`;
    const backgroundSize = '100%';

    const width = parseInt(String(signature.width), 10);
    const height = parseInt(String(signature.height), 10);

    const imageDims: Dimensions =
      rotation === 90 || rotation === 270
        ? {
            width: height,
            height: width,
          }
        : {
            width,
            height,
          };

    return {
      ...scaleSignatureToFit(imageDims, PREVIEW_AREA_DIMENSIONS),
      backgroundImage,
      backgroundSize,
    };
  }, [signature, getSignatureUrl, contrast, rotation]);

  useEffect(() => {
    if (signature) {
      for (
        let tempContrast = CONSTANTS.CONTRAST_MIN_THRESHOLD;
        tempContrast <= CONSTANTS.CONTRAST_MAX_THRESHOLD;
        tempContrast += CONSTANTS.CONTRAST_STEP_AMOUNT
      ) {
        const cacheKey = `c${tempContrast}d${rotation}`;
        const image = new Image();
        image.src = getSignatureUrl(signature, tempContrast, rotation);
        cache.set(cacheKey, image);
      }
    }
  }, [signature, rotation, cache, getSignatureUrl]);

  const handleRotate = useCallback(() => {
    const increasedRotation = rotation + CONSTANTS.ROTATE_DEGREES_AMOUNT;
    onPreviewChange({
      rotation: increasedRotation >= 360 ? 0 : increasedRotation,
    });
  }, [onPreviewChange, rotation]);

  const handleDelete = useCallback(() => {
    clearUploadedSignature();
  }, [clearUploadedSignature]);

  const decreaseContrast = useCallback(() => {
    if (contrast > CONSTANTS.CONTRAST_MIN_THRESHOLD) {
      onPreviewChange({
        contrast: contrast - CONSTANTS.CONTRAST_STEP_AMOUNT,
      });
    }
  }, [contrast, onPreviewChange]);

  const increaseContrast = useCallback(() => {
    if (contrast < CONSTANTS.CONTRAST_MAX_THRESHOLD) {
      onPreviewChange({
        contrast: contrast + CONSTANTS.CONTRAST_STEP_AMOUNT,
      });
    }
  }, [contrast, onPreviewChange]);

  const handleSelect = useCallback(
    ({ left, top, width, height }: Bounds) => {
      const preview = previewRef.current;
      if (preview) {
        const previewBounds = preview.getBoundingClientRect();

        const preScaledCrop = {
          x: left - previewBounds.left,
          y: top - previewBounds.top,
          width,
          height,
        };

        if (preScaledCrop.width > 0 && preScaledCrop.height > 0) {
          const scaledParams = SignatureEditAreaHelper.scaleCrop(
            previewBounds,
            {
              width: String(signature.width),
              height: String(signature.height),
            },
            preScaledCrop,
            rotation,
          );

          onPreviewChange({
            bounds: {
              left: scaledParams.x,
              top: scaledParams.y,
              width: scaledParams.width,
              height: scaledParams.height,
            },
          });
        } else {
          onPreviewChange({
            bounds: null,
          });
        }
      }
    },
    [signature, rotation, onPreviewChange],
  );

  return (
    <div className={styles.editArea}>
      <Croppable onSelect={handleSelect}>
        <div
          role="img"
          aria-label={intl.formatMessage(messages.signaturePreview)}
          data-qa-ref={'signing-modal--preview-image'}
          className={styles.previewImage}
          style={previewStyle}
          ref={previewRef}
        />
      </Croppable>

      {isNewSignerApp ? (
        <ChipButtonsWrapper>
          <Chip onClick={handleRotate} size="small">
            <Chip.IconAccessory>
              <UIIcon src={ShapeRotateRightLine} />
            </Chip.IconAccessory>
            <Chip.Content>
              <FormattedMessage
                id="eaf0a4bdf722bfeb8098b933650c51f5d152b9cd39f5911f7c7d2f7819fad6b7"
                description="Text for a button which rotates the uploaded signature image when clicked."
                defaultMessage="Rotate"
              />
            </Chip.Content>
          </Chip>

          <Chip size="small">
            <IconButton
              variant={'transparent'}
              size={'small'}
              onClick={decreaseContrast}
              disabled={contrast <= CONSTANTS.CONTRAST_MIN_THRESHOLD}
            >
              <Chip.IconAccessory>
                <UIIcon src={ZoomOutLine} />
              </Chip.IconAccessory>
            </IconButton>
            <Chip.Content>
              <FormattedMessage
                id="acb86942110744f47ef1837a8bd55949b3fdc63b616f0963280cbf901c9f11f0"
                description="Text for a button that changes the contrast for the uplodaed signature image when clicked."
                defaultMessage="Contrast"
              />
            </Chip.Content>

            <IconButton
              variant={'transparent'}
              size={'small'}
              onClick={increaseContrast}
              disabled={contrast >= CONSTANTS.CONTRAST_MAX_THRESHOLD}
            >
              <Chip.IconAccessory>
                <UIIcon src={ZoomInLine} />
              </Chip.IconAccessory>
            </IconButton>
          </Chip>

          <Chip onClick={handleDelete} size="small">
            <Chip.IconAccessory>
              <UIIcon src={DeleteLine} />
            </Chip.IconAccessory>
            <Chip.Content>
              <FormattedMessage
                id="0dcdfaa3e48eb4f81693ccf07e8318070d60a7c2be451cb7b135f9202cdeb0bc"
                description="Text for a button which removes the uplodaed signature image when clicked."
                defaultMessage="Clear"
              />
            </Chip.Content>
          </Chip>
        </ChipButtonsWrapper>
      ) : (
        <div className={styles.buttons}>
          <Text
            id="signature-contrast-label"
            className={styles.contrastLabel}
            size="small"
            isBold
          >
            <FormattedMessage
              id="8e6ad3eb2444460b3034fbf1726b2e53dbd676542339f446df98daa0f1dd1aa0"
              description="Text label in signer modal, where signer can adjust contrast of typed/drawn signature/initials"
              defaultMessage="Contrast"
            />
          </Text>
          <IconButton
            ref={decreaseContrastRef}
            variant="borderless"
            className={styles.decreaseContrast}
            aria-label={intl.formatMessage(messages.decreaseContrast, {
              contrastPercentage,
            })}
            disabled={contrast <= CONSTANTS.CONTRAST_MIN_THRESHOLD}
            onClick={decreaseContrast}
          >
            <UIIcon src={ZoomOutLine} />
          </IconButton>
          <Text
            aria-labelledby="signature-contrast-label"
            className={styles.contrastValue}
            size="small"
            isBold
          >
            {intl.formatNumber(contrastPercentage, { style: 'percent' })}
          </Text>
          <IconButton
            ref={increaseContrastRef}
            variant="borderless"
            aria-label={intl.formatMessage(messages.increaseContrast, {
              contrastPercentage,
            })}
            disabled={contrast >= CONSTANTS.CONTRAST_MAX_THRESHOLD}
            onClick={increaseContrast}
          >
            <UIIcon src={ZoomInLine} />
          </IconButton>
          {isMobile ? (
            <>
              <ButtonsDivider />
              <IconButton
                variant="borderless"
                aria-label={intl.formatMessage(messages.rotateAriaLabel, {
                  rotation,
                })}
                onClick={handleRotate}
              >
                <UIIcon src={RotateRightLine} />
              </IconButton>
              <ButtonsDivider />
              <IconButton
                variant="borderless"
                aria-label={intl.formatMessage(messages.deleteAriaLabel)}
                onClick={handleDelete}
              >
                <UIIcon src={DeleteLine} />
              </IconButton>
            </>
          ) : (
            <>
              <ButtonsDivider />
              <Button
                size="small"
                variant="borderless"
                className={styles.textButton}
                aria-label={intl.formatMessage(messages.rotateAriaLabel, {
                  rotation,
                })}
                withIconLeft={<UIIcon src={RotateRightLine} />}
                onClick={handleRotate}
              >
                <FormattedMessage
                  id="916e7050a90872fe6cc404ba2ad2090bb3daa8d9630be4460897efc216f23cb8"
                  description="Text for a button which rotates an image clockwise when pressed."
                  defaultMessage="Rotate"
                />
              </Button>
              <ButtonsDivider />
              <Button
                size="small"
                variant="borderless"
                className={styles.textButton}
                aria-label={intl.formatMessage(messages.deleteAriaLabel)}
                withIconLeft={<UIIcon src={DeleteLine} />}
                onClick={handleDelete}
              >
                <FormattedMessage
                  id="4397f5847118f4d3269a198fab14fca373c46cb629fd74c644f6d7f53ead6904"
                  description="Text for a button which deletes an image when pressed."
                  defaultMessage="Delete"
                />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SignatureEditArea;
